import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import useSettingsStore from "../store/useSettingsStore";
import { Menu, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const navLinks = [
  { path: "/", label: "Главная" },
  { path: "/catalog", label: "Каталог" },
  { path: "/configurator", label: "Подбор" },
  { path: "/blog", label: "Блог" },
  { path: "/reviews", label: "Отзывы" },
  { path: "/about", label: "О нас" },
  { path: "/contacts", label: "Контакты" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const settings = useSettingsStore((s) => s.settings);
  const content = useSettingsStore((s) => s.content);

  const logoUrl = settings?.logo_url
    ? settings.logo_url.startsWith("http")
      ? settings.logo_url
      : `${BACKEND_URL}${settings.logo_url}`
    : "";
  const companyName = content?.header?.company_name || settings?.site_title || "Авто Маркет";
  const contactBtn = content?.header?.contact_button || "Связаться";

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-[200] transition-all duration-300"
        style={{ background: "rgba(10,25,47,0.85)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}
        data-testid="header"
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0" data-testid="header-logo">
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt={companyName}
                  className="h-12 sm:h-14 md:h-16 w-auto logo-glow"
                  loading="eager"
                />
              )}
              <span className="text-lg sm:text-xl md:text-2xl font-semibold text-[var(--accent)] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {companyName}
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-300 rounded-full ${
                    location.pathname === link.path
                      ? "text-[var(--accent)] bg-[var(--accent-muted)]"
                      : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-white/5"
                  }`}
                  data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <Link
                to="/contacts"
                className="hidden md:inline-flex btn-premium btn-primary"
                data-testid="header-contact-btn"
              >
                {contactBtn}
              </Link>
              <button
                onClick={() => setOpen(!open)}
                className="lg:hidden p-2 text-[var(--accent)] relative z-[210]"
                data-testid="burger-menu-btn"
                aria-label="Menu"
              >
                {open ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu — rendered as portal-like sibling, above header */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[205]" data-testid="mobile-menu-overlay">
          {/* Dark backdrop */}
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          {/* Menu panel */}
          <div
            className="absolute top-16 left-0 right-0 bottom-0 overflow-y-auto"
            style={{
              background: "var(--surface)",
              borderTop: "2px solid var(--accent)",
              boxShadow: "0 -2px 20px rgba(212,196,168,0.15), 0 8px 40px rgba(0,0,0,0.6)",
            }}
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col p-6 gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setOpen(false)}
                  className={`px-5 py-4 text-lg font-medium transition-all duration-200 rounded-xl ${
                    location.pathname === link.path
                      ? "text-[var(--accent)] bg-[var(--accent-muted)]"
                      : "text-[var(--text-primary)] hover:text-[var(--accent)] hover:bg-white/5"
                  }`}
                  data-testid={`mobile-nav-${link.path.replace("/", "") || "home"}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contacts"
                onClick={() => setOpen(false)}
                className="mt-6 btn-premium btn-primary text-center text-lg"
                data-testid="mobile-contact-btn"
              >
                {contactBtn}
              </Link>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
